"use client";

import * as api from "@/src/api/sdk.gen";
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";

import {
  type LessonLanguage,
  type PreferredLanguageKey,
  PREFERRED_LANGUAGE_KEY_TO_LABEL,
  preferredLanguageKeyFromLabel,
} from "@/lib/lesson-language";

export type UserSettingsCourse = {
    settingsId: number;
    courseId: number | string;
    courseUuid: string;
    courseTitle: string;
    domainId: number | null;
    futureGoalId: number | null;
    departmentId?: number | null;
    learningPace?: string | null;
    subscriptionStatus?: string;
    language: LessonLanguage;
    domains: Array<{ id: number; label: string }>;
    futureGoals: Array<{ id: number; label: string }>;
    departments?: Array<{ id: number; label: string }>;
};

type Props = {
    courses: UserSettingsCourse[];
};

type SaveState = { status: "idle" } | { status: "saving" } | { status: "saved" } | { status: "error"; message: string };

function toPreferredLanguageKey(language: LessonLanguage): PreferredLanguageKey {
    return preferredLanguageKeyFromLabel(language);
}

export default function UserSettingsManager({ courses }: Props) {
    const queryClient = useQueryClient();
    const [initialCourses, setInitialCourses] = useState(courses);
    const [editableCourses, setEditableCourses] = useState(courses);
    const [saveState, setSaveState] = useState<Record<number, SaveState>>({});

    useEffect(() => {
        setInitialCourses(courses);
        setEditableCourses(courses);
    }, [courses]);

    const updateSelection = (
        settingsId: number,
        field: "domainId" | "futureGoalId" | "departmentId" | "preferredLanguage",
        value: string,
    ) => {
        if (field === "preferredLanguage") {
            const nextLanguage: PreferredLanguageKey = value === "arabic" ? "arabic" : "english";
            setEditableCourses((prev) =>
                prev.map((item) =>
                    item.settingsId === settingsId
                        ? { ...item, language: PREFERRED_LANGUAGE_KEY_TO_LABEL[nextLanguage] }
                        : item,
                ),
            );
            setSaveState((prev) => {
                if (prev[settingsId]?.status === "saving") return prev;
                return { ...prev, [settingsId]: { status: "idle" } };
            });
            return;
        }
        const parsed = value !== "" ? Number(value) : null;
        setEditableCourses((prev) =>
            prev.map((item) => (item.settingsId === settingsId ? { ...item, [field]: parsed } : item)),
        );
        setSaveState((prev) => {
            if (prev[settingsId]?.status === "saving") return prev;
            return { ...prev, [settingsId]: { status: "idle" } };
        });
    };

    const handleSave = async (course: UserSettingsCourse) => {
        setSaveState((prev) => ({ ...prev, [course.settingsId]: { status: "saving" } }));

        try {
            const { error, response } = await api.updateUserSettingsApiV1UsersSettingsPatch({
                body: {
                    course_id: course.courseId,
                    domain_id: course.domainId,
                    future_goal_id: course.futureGoalId,
                    department_id: course.departmentId,
                    language: course.language,
                },
            });

            if (error) {
                const body = error as { error?: string };
                setSaveState((prev) => ({
                    ...prev,
                    [course.settingsId]: {
                        status: "error",
                        message:
                            body.error ??
                            `Failed to save settings (status ${response?.status?.toString()}). Please try again.`,
                    },
                }));
                return;
            }

            void queryClient.invalidateQueries({ queryKey: ["course-user-settings"] });
            void queryClient.invalidateQueries({ queryKey: ["user-profile"] });
            void queryClient.invalidateQueries({ queryKey: ["student-courses-list"] });

            setInitialCourses((prev) =>
                prev.map((item) =>
                    item.settingsId === course.settingsId
                        ? {
                              ...item,
                              domainId: course.domainId,
                              futureGoalId: course.futureGoalId,
                              departmentId: course.departmentId,
                              language: course.language,
                          }
                        : item,
                ),
            );
            setSaveState((prev) => ({ ...prev, [course.settingsId]: { status: "saved" } }));
        } catch {
            setSaveState((prev) => ({
                ...prev,
                [course.settingsId]: {
                    status: "error",
                    message: "Network error while saving. Please try again.",
                },
            }));
        }
    };

    const handleReset = (settingsId: number) => {
        const baseline = initialCourses.find((item) => item.settingsId === settingsId);
        if (!baseline) return;
        setEditableCourses((prev) =>
            prev.map((item) =>
                item.settingsId === settingsId
                    ? {
                          ...item,
                          domainId: baseline.domainId,
                          futureGoalId: baseline.futureGoalId,
                          departmentId: baseline.departmentId,
                          language: baseline.language,
                      }
                    : item,
            ),
        );
        setSaveState((prev) => ({ ...prev, [settingsId]: { status: "idle" } }));
    };

    if (editableCourses.length === 0) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white/95 p-8 text-center shadow-sm">
                <p className="text-sm text-slate-500">No course settings available.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {editableCourses.map((course) => {
                const baseline = initialCourses.find((item) => item.settingsId === course.settingsId);
                const hasUnsavedChanges =
                    baseline != null &&
                    (
                        course.domainId !== baseline.domainId ||
                        course.futureGoalId !== baseline.futureGoalId ||
                        course.departmentId !== baseline.departmentId ||
                        course.language !== baseline.language
                    );
                const state = saveState[course.settingsId] ?? { status: "idle" };
                const preferredLanguageKey = toPreferredLanguageKey(course.language);

                return (
                    <section
                        key={course.settingsId}
                        className="rounded-2xl border border-slate-200 bg-white/95 p-6 shadow-sm transition-shadow hover:shadow-md"
                    >
                        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
                            COURSE PREFERENCES
                        </div>
                        <h2 className="mt-1 text-xl font-bold text-slate-900">
                            {course.courseTitle}
                        </h2>

                        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <div>
                                <label
                                    htmlFor={`domain-${course.settingsId.toString()}`}
                                    className="text-xs font-bold uppercase tracking-wide text-slate-600"
                                >
                                    CHOSEN DOMAIN
                                </label>
                                <div className="relative mt-2">
                                    <select
                                        id={`domain-${course.settingsId.toString()}`}
                                        className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 pr-10 text-sm font-medium text-slate-900 shadow-sm outline-none transition hover:border-slate-300 hover:bg-white focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                                        value={course.domainId ?? ""}
                                        onChange={(event) => updateSelection(course.settingsId, "domainId", event.target.value)}
                                    >
                                        <option value="">Select a domain</option>
                                        {course.domains.map((domain) => (
                                            <option key={domain.id} value={domain.id}>
                                                {domain.label}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                </div>
                            </div>

                            {course.departments && course.departments.length > 0 ? (
                                <div>
                                    <label
                                        htmlFor={`department-${course.settingsId.toString()}`}
                                        className="text-xs font-bold uppercase tracking-wide text-slate-600"
                                    >
                                        DEPARTMENT
                                    </label>
                                    <div className="relative mt-2">
                                        <select
                                            id={`department-${course.settingsId.toString()}`}
                                            className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 pr-10 text-sm font-medium text-slate-900 shadow-sm outline-none transition hover:border-slate-300 hover:bg-white focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                                            value={course.departmentId ?? ""}
                                            onChange={(event) => updateSelection(course.settingsId, "departmentId", event.target.value)}
                                        >
                                            <option value="">Select a department</option>
                                            {course.departments.map((dept) => (
                                                <option key={dept.id} value={dept.id}>
                                                    {dept.label}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    </div>
                                </div>
                            ) : course.futureGoals && course.futureGoals.length > 0 ? (
                                <div>
                                    <label
                                        htmlFor={`goal-${course.settingsId.toString()}`}
                                        className="text-xs font-bold uppercase tracking-wide text-slate-600"
                                    >
                                        FUTURE GOAL
                                    </label>
                                    <div className="relative mt-2">
                                        <select
                                            id={`goal-${course.settingsId.toString()}`}
                                            className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 pr-10 text-sm font-medium text-slate-900 shadow-sm outline-none transition hover:border-slate-300 hover:bg-white focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                                            value={course.futureGoalId ?? ""}
                                            onChange={(event) => updateSelection(course.settingsId, "futureGoalId", event.target.value)}
                                        >
                                            <option value="">Select a goal</option>
                                            {course.futureGoals.map((goal) => (
                                                <option key={goal.id} value={goal.id}>
                                                    {goal.label}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        {course.departments && course.departments.length > 0 && course.futureGoals && course.futureGoals.length > 0 && (
                            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <div>
                                    <label
                                        htmlFor={`goal-${course.settingsId.toString()}`}
                                        className="text-xs font-bold uppercase tracking-wide text-slate-600"
                                    >
                                        FUTURE GOAL
                                    </label>
                                    <div className="relative mt-2">
                                        <select
                                            id={`goal-${course.settingsId.toString()}`}
                                            className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 pr-10 text-sm font-medium text-slate-900 shadow-sm outline-none transition hover:border-slate-300 hover:bg-white focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                                            value={course.futureGoalId ?? ""}
                                            onChange={(event) => updateSelection(course.settingsId, "futureGoalId", event.target.value)}
                                        >
                                            <option value="">Select a goal</option>
                                            {course.futureGoals.map((goal) => (
                                                <option key={goal.id} value={goal.id}>
                                                    {goal.label}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-5">
                            <label
                                htmlFor={`language-${course.settingsId.toString()}`}
                                className="text-xs font-bold uppercase tracking-wide text-slate-600"
                            >
                                PREFERRED LESSON LANGUAGE
                            </label>
                            <div className="relative mt-2 sm:max-w-md">
                                <select
                                    id={`language-${course.settingsId.toString()}`}
                                    className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 pr-10 text-sm font-medium text-slate-900 shadow-sm outline-none transition hover:border-slate-300 hover:bg-white focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                                    value={preferredLanguageKey}
                                    onChange={(event) =>
                                        updateSelection(course.settingsId, "preferredLanguage", event.target.value)
                                    }
                                >
                                    <option value="english">English</option>
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            </div>
                            <p className="mt-2 text-xs text-slate-500">
                                Language applies only to upcoming lessons and does not change lessons already created.
                            </p>
                        </div>

                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <button
                                type="button"
                                onClick={() => handleSave(course)}
                                disabled={state.status === "saving" || !hasUnsavedChanges}
                                className="rounded-xl bg-[#738399] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#607187] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
                            >
                                {state.status === "saving" ? "Saving..." : "Save changes"}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleReset(course.settingsId)}
                                disabled={state.status === "saving" || !hasUnsavedChanges}
                                className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Reset
                            </button>
                            {state.status === "saved" ? (
                                <p className="rounded-lg bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700 font-medium">
                                    Your settings have been saved.
                                </p>
                            ) : null}
                            {state.status === "error" ? (
                                <p className="rounded-lg bg-red-50 px-3 py-1.5 text-sm text-red-700 font-medium">{state.message}</p>
                            ) : null}
                        </div>
                    </section>
                );
            })}
        </div>
    );
}

